import { ChangeEvent, ComponentProps, ReactNode, useCallback, useMemo } from 'react';
import { useController, UseControllerReturn, useFormContext } from 'react-hook-form';
import { RadioButton } from '@dtpk-cc/components';

type RadioButtonGroupProps = ComponentProps<typeof RadioButton.Group>;

type RequiredFromRadioButtonGroupProps = Required<Pick<RadioButtonGroupProps, 'name'>>;
type OmitFromRadioButtonGroupProps = Omit<RadioButtonGroupProps, 'onChange' | 'required' | 'id'>;
type ControlledRadioButtonGroupProps = OmitFromRadioButtonGroupProps &
    RequiredFromRadioButtonGroupProps & {
        render: (ctrl: UseControllerReturn) => ReactNode;
    };

export const ControlledRadioButtonGroup = ({ name, render, ...inputProps }: ControlledRadioButtonGroupProps) => {
    const { control } = useFormContext();
    const { field, fieldState, formState } = useController({
        control,
        name,
        rules: { required: true },
    });

    const internalHandleChange = useCallback(
        (_: string, event?: ChangeEvent<HTMLInputElement>) => {
            field.onChange(event);
        },
        [field]
    );

    const internalChildren = useMemo(
        () => render({ field, fieldState, formState }),
        [render, field, fieldState, formState]
    );

    return (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <RadioButton.Group onChange={internalHandleChange} name={field.name} {...inputProps}>
            {internalChildren}
        </RadioButton.Group>
    );
};
